import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { registerService, recordRegistrationOTPSent } from '../services/registerService'
import { authService } from '../services/authService'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import {
  clearLineBridgeFlowContext,
  closeFirebaseAuthBridge,
  FIREBASE_AUTH_BRIDGE_LINE_METHOD,
  loadLineBridgeFlowContext,
  redirectToFirebaseAuthBridgeVerify,
  reportFirebaseSmsBilling,
  verifyOTPViaFirebaseAuthBridge,
} from '../lib/firebaseAuthBridge'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeAssetUrl } from '../utils/themeUtils'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface RegisterOTPPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

interface LocationState {
  phoneNumber: string
  sessionId: string
  expiresAt: number
  disableResend?: boolean // 🎯 Prevent OTP resend to avoid SMS cost abuse
  method?: string
  provider?: string
}

const toThaiPhoneDisplay = (phoneNumber?: string) => {
  if (!phoneNumber) return ''
  const cleaned = String(phoneNumber).trim()
  if (cleaned.startsWith('+66') && cleaned.length >= 12) {
    return `0${cleaned.slice(3)}`
  }
  return cleaned
}

const OTP_STATE_STORAGE_KEY = 'huay-register-otp-state'

const readPersistedOtpState = (): LocationState | null => {
  try {
    const raw = localStorage.getItem(OTP_STATE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LocationState
    if (!parsed?.phoneNumber || !parsed?.sessionId || !parsed?.expiresAt) {
      return null
    }
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(OTP_STATE_STORAGE_KEY)
      return null
    }
    return {
      ...parsed,
      phoneNumber: toThaiPhoneDisplay(parsed.phoneNumber),
    }
  } catch {
    return null
  }
}

const persistOtpState = (state: LocationState) => {
  try {
    localStorage.setItem(OTP_STATE_STORAGE_KEY, JSON.stringify({
      ...state,
      phoneNumber: toThaiPhoneDisplay(state.phoneNumber),
    }))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpState = () => {
  try {
    localStorage.removeItem(OTP_STATE_STORAGE_KEY)
  } catch {
    // Ignore storage cleanup failures.
  }
}

// Global Firebase types
declare global {
  interface Window {
    phoneConfirmation?: {
      confirm: (verificationCode: string) => Promise<{
        user: { uid: string }
      }>
    } | null
  }
}

const RegisterOTPPage: React.FC<RegisterOTPPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const lineBridgeHandledRef = useRef(false)
  const routeState = location.state as LocationState | null
  const state = (
    (routeState?.phoneNumber && routeState?.sessionId
      ? {
          ...routeState,
          phoneNumber: toThaiPhoneDisplay(routeState.phoneNumber),
        }
      : readPersistedOtpState()) || {
        phoneNumber: '',
        sessionId: '',
        expiresAt: 0,
      }
  ) as LocationState

  // Get theme-specific assets path
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }

  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme

  // OTP form state
  const [otpCode, setOtpCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const [canResend, setCanResend] = useState(false)

  useEffect(() => {
    if (lineBridgeHandledRef.current) {
      return
    }

    const lineBridge = searchParams.get('lineBridge')
    if (!lineBridge) {
      return
    }

    lineBridgeHandledRef.current = true
    const tenantId = authService.getTenantId() || themeConfig?.tenantId || ''
    const lineFlow = loadLineBridgeFlowContext()
    const apiBaseUrl = getApiBaseUrl()

    const clearLineBridgeQuery = () => {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('lineBridge')
      nextParams.delete('flowId')
      nextParams.delete('firebaseUid')
      nextParams.delete('sessionId')
      nextParams.delete('errorMessage')
      setSearchParams(nextParams, { replace: true })
    }

    if (lineBridge === 'sent' && lineFlow) {
      recordRegistrationOTPSent(state.phoneNumber || toThaiPhoneDisplay(lineFlow.phoneNumber)).catch(() => {})
      reportFirebaseSmsBilling({
        apiBaseUrl: lineFlow.apiBaseUrl || apiBaseUrl,
        tenantId: lineFlow.tenantId || tenantId,
        phoneNumber: lineFlow.phoneNumber,
        status: 'sent',
        messageType: 'registration',
        source: 'frontend-classic-registration-bridge-line',
      })
      clearLineBridgeQuery()
      return
    }

    if (lineBridge === 'verified') {
      clearLineBridgeFlowContext()
      clearPersistedOtpState()
      clearLineBridgeQuery()

      const sessionId = searchParams.get('sessionId') || state.sessionId || lineFlow?.sessionId || ''
      const phoneNumber = state.phoneNumber || toThaiPhoneDisplay(lineFlow?.phoneNumber)

      void Swal.fire({
        icon: 'success',
        title: 'ยืนยัน OTP สำเร็จ',
        text: 'จะนำคุณไปยังขั้นตอนการกรอกข้อมูลส่วนตัว',
        confirmButtonText: 'ดำเนินการต่อ',
        confirmButtonColor: '#28a745',
        timer: 2000,
        timerProgressBar: true,
      }).then(() => {
        navigate('/register/complete', {
          state: {
            phoneNumber,
            sessionId,
            smsVerified: true,
          },
          replace: true,
        })
      })
      return
    }

    if (lineBridge === 'error') {
      clearLineBridgeFlowContext()
      clearLineBridgeQuery()
      const errorMessage = searchParams.get('errorMessage') || 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ'
      void Swal.fire({
        icon: 'error',
        title: 'ส่ง SMS ล้มเหลว',
        text: errorMessage,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545',
      }).then(() => {
        navigate('/register', { replace: true })
      })
    }
  }, [navigate, searchParams, setSearchParams, state.phoneNumber, state.sessionId, themeConfig?.tenantId])

  // Check if state exists, redirect if not
  useEffect(() => {
    if (searchParams.get('lineBridge')) {
      return
    }

    console.log('🔍 RegisterOTPPage received state:', {
      hasState: !!state,
      phoneNumber: state?.phoneNumber,
      sessionId: state?.sessionId,
      method: state?.method,
      provider: state?.provider,
      expiresAt: state?.expiresAt,
      fullState: state
    })
    
    if (!state?.phoneNumber || !state?.sessionId) {
      clearPersistedOtpState()
      console.log('❌ RegisterOTPPage state validation failed, redirecting to /register')
      console.log('❌ Missing required fields:', {
        phoneNumber: !!state?.phoneNumber,
        sessionId: !!state?.sessionId,
        state: state
      })
      Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณาเริ่มกระบวนการสมัครสมาชิกใหม่',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      }).then(() => {
        navigate('/register', { replace: true })
      })
      return
    }
    
    console.log('✅ RegisterOTPPage state validation passed')
    persistOtpState(state)

    // Calculate initial time left
    const now = Date.now()
    const remaining = Math.max(0, Math.floor((state.expiresAt - now) / 1000))
    
    console.log('⏰ OTP expiry calculation:', {
      now: now,
      expiresAt: state.expiresAt,
      remaining: remaining,
      expiresAtDate: new Date(state.expiresAt).toISOString(),
      nowDate: new Date(now).toISOString()
    })
    
    // console.log('⏰ Setting timeLeft to:', remaining)
    setTimeLeft(remaining)
    setCanResend(remaining === 0)
  }, [state, navigate, searchParams])

  // Countdown timer with expiry redirect
  useEffect(() => {
    // console.log('⏰ Countdown timer check - timeLeft:', timeLeft)
    
    // Don't check expiry if timeLeft is still 0 (initial state)
    if (timeLeft === 0) {
      // console.log('⏰ timeLeft is 0 (initial state), skipping expiry check')
      return
    }
    
    if (timeLeft <= 0) {
      setCanResend(true)
      
      // 🔄 Auto-redirect when OTP expires
      if (timeLeft === 0) {
        console.log('⏰ OTP expired, redirecting to register page...')
        Swal.fire({
          icon: 'warning',
          title: 'รหัส OTP หมดอายุ',
          text: 'รหัส OTP หมดอายุแล้ว กรุณาขอรหัสใหม่',
          confirmButtonText: 'ขอรหัสใหม่',
          confirmButtonColor: '#ffc107',
          timer: 3000,
          timerProgressBar: true
        }).then(() => {
          clearPersistedOtpState()
          navigate('/register', { replace: true })
        })
      }
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1
        if (newTime <= 0) {
          setCanResend(true)
          return 0
        }
        return newTime
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft, navigate])

  // Handle OTP input - only numbers, max 6 digits
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    if (value.length <= 6) {
      setOtpCode(value)
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otpCode.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกรหัส OTP',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }

    if (otpCode.length !== 6) {
      await Swal.fire({
        icon: 'warning',
        title: 'รหัส OTP ไม่ถูกต้อง',
        text: 'รหัส OTP ต้องมี 6 หลัก',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }

    // 🔄 Check if OTP has expired before submission
    const now = Date.now()
    if (now >= state.expiresAt) {
      console.log('⏰ OTP expired during submission, redirecting to register page...')
      await Swal.fire({
        icon: 'warning',
        title: 'รหัส OTP หมดอายุ',
        text: 'รหัส OTP หมดอายุแล้ว กรุณาขอรหัสใหม่',
        confirmButtonText: 'ขอรหัสใหม่',
        confirmButtonColor: '#ffc107'
      }).then(() => {
        clearPersistedOtpState()
        navigate('/register', { replace: true })
      })
      return
    }

    setIsSubmitting(true)

    try {
      console.log('🔐 Verifying OTP:', {
        phone: state.phoneNumber,
        sessionId: state.sessionId,
        otpLength: otpCode.length,
        timeLeft: Math.floor((state.expiresAt - now) / 1000)
      })

      let response;
      
      // 🎯 Different verification methods based on provider
      if (state.method === FIREBASE_AUTH_BRIDGE_LINE_METHOD) {
        console.log('📱 Using LINE full-page redirect Firebase auth bridge verification...')
        const lineFlow = loadLineBridgeFlowContext()
        if (!lineFlow) {
          throw new Error('ไม่พบ session สำหรับยืนยัน OTP กรุณาเริ่มสมัครใหม่')
        }

        redirectToFirebaseAuthBridgeVerify({
          flowId: lineFlow.flowId,
          otpCode,
          returnUrl: `${window.location.origin}/register/otp`,
        })
        return
      }

      if (state.method === 'firebase-auth-bridge') {
        console.log('🪟 Using central Firebase auth bridge verification...')

        try {
          const result = await verifyOTPViaFirebaseAuthBridge(otpCode)
          console.log('✅ Firebase bridge OTP verified successfully:', result.payload?.uid)

          response = {
            success: true,
            data: {
              phoneVerified: true,
              nextStep: 'complete_registration',
              firebaseUid: result.payload?.uid
            }
          }
          closeFirebaseAuthBridge()
        } catch (bridgeError: unknown) {
          console.error('❌ Firebase bridge OTP verification failed:', bridgeError)
          const error = bridgeError as Error
          response = {
            success: false,
            error: 'FIREBASE_BRIDGE_VERIFICATION_FAILED',
            message: error.message || 'รหัส OTP ไม่ถูกต้อง'
          }
        }
      } else if (state.method === 'firebase-client-sdk' || state.provider === 'firebase') {
        // 🔥 Firebase Client SDK verification
        console.log('🔥 Using Firebase Client SDK verification...')
        
        try {
          if (!window.phoneConfirmation) {
            throw new Error('Firebase phone confirmation not available')
          }
          
          const credential = await window.phoneConfirmation.confirm(otpCode)
          console.log('✅ Firebase OTP verified successfully:', credential.user.uid)
          
          response = {
            success: true,
            data: {
              phoneVerified: true,
              nextStep: 'complete_registration',
              firebaseUid: credential.user.uid
            }
          }
        } catch (firebaseError: unknown) {
          console.error('❌ Firebase OTP verification failed:', firebaseError)
          const error = firebaseError as any
          response = {
            success: false,
            error: error?.code || 'FIREBASE_VERIFICATION_FAILED',
            message: error?.message || 'รหัส OTP ไม่ถูกต้อง'
          }
        }
      } else {
        // 📱 Twilio/Other providers - use user-auth verification
        console.log('📱 Using user-auth service verification...')
        response = await registerService.verifySmsCode(
          state.phoneNumber,
          otpCode,
          state.sessionId
        )
      }

      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: 'ยืนยัน OTP สำเร็จ',
          text: 'จะนำคุณไปยังขั้นตอนการกรอกข้อมูลส่วนตัว',
          confirmButtonText: 'ดำเนินการต่อ',
          confirmButtonColor: '#28a745',
          timer: 2000,
          timerProgressBar: true
        })

        // Navigate to username/password form
      clearPersistedOtpState()
        navigate('/register/complete', {
          state: {
            phoneNumber: state.phoneNumber,
            sessionId: state.sessionId,
            smsVerified: true
          },
          replace: true
        })

      } else {
        await Swal.fire({
          icon: 'error',
          title: 'รหัส OTP ไม่ถูกต้อง',
          text: response.error || 'กรุณาตรวจสอบรหัส OTP และลองใหม่อีกครั้ง',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
      }

    } catch (error) {
      console.error('❌ OTP verification error:', error)

      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถยืนยัน OTP ได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (!canResend) return

    try {
      setIsSubmitting(true)

      const response = await registerService.sendPhoneVerification(state.phoneNumber)

      if (response.success) {
        // Update state with new session info
        const newState = {
          ...state,
          sessionId: response.data?.sessionId || response.data?.verificationId || state.sessionId,
          expiresAt: response.data?.expiresAt || (Date.now() + 300000)
        }

        // Update location state
        navigate('/register/otp', {
          state: newState,
          replace: true
        })

        // Reset timer
        setTimeLeft(300) // 5 minutes
        setCanResend(false)

        await Swal.fire({
          icon: 'success',
          title: 'ส่ง OTP ใหม่สำเร็จ',
          text: 'เราได้ส่งรหัส OTP ใหม่ไปยังหมายเลขโทรศัพท์ของคุณแล้ว',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#28a745',
          timer: 3000
        })

      } else {
        await Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถส่ง OTP ใหม่ได้',
          text: response.error || 'เกิดข้อผิดพลาดในการส่ง SMS',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
      }

    } catch (error) {
      console.error('❌ Resend OTP error:', error)

      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถส่ง OTP ใหม่ได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Don't render if no state
  if (!state?.phoneNumber) {
    return null
  }

  return (
    <div className="content py-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 col-lg-6 offset-lg-3">
            <div className="box-login text-center bgi-2 bdr-10 c-white bx-sd p-2 mb-3">
              <div className="bgc-16 bdr-10 p-3 p-lg-4">
                <div className="box-regis w-100">
                  <div className="img-regis mx-auto">
                    <img 
                      src={getAssetUrl('/img/logo-m.png')} 
                      alt={branding.siteName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getAssetUrl('/img/logo-m.png')
                      }}
                    />
                  </div>
                </div>

                <h3 className="txt-user mb-1 text-center t-medium">สมัครสมาชิก</h3>

                <div className="wrap justify-content-center mb-3">
                  <small className="bgg-2 bdr-10 c-dark px-2 t-regular me-1">
                    STEP 2
                  </small>
                  <h6 className="text-center">
                    ยืนยันรหัส OTP ({state.phoneNumber})
                  </h6>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-inp bdr-10">
                    <div className="icon ps-1">
                      <i className="fas fa-phone-alt"></i>
                    </div>
                    <input
                      type="text"
                      id="otpCode"
                      value={otpCode}
                      onChange={handleOtpChange}
                      placeholder="กรอกเลข OTP"
                      maxLength={6}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <small className="">
                    หมายเลขโอทีพีได้จากข้อความเบอร์มือถือที่ใช้สมัคร
                  </small>

                  {/* Additional info for better UX */}
                  <div className="mt-2 mb-3">
                    {timeLeft > 0 ? (
                      <small className="text-danger d-block">
                        รหัส OTP หมดอายุใน {formatTime(timeLeft)}
                      </small>
                    ) : (
                      <small className="text-warning d-block">
                        รหัส OTP หมดอายุแล้ว
                      </small>
                    )}
                    
                    {canResend && !state.disableResend && (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isSubmitting}
                        className="btn btn-link text-decoration-none p-0 mt-1"
                        style={{ 
                          color: '#007bff',
                          fontSize: '0.875rem'
                        }}
                      >
                        <i className="fas fa-redo me-1"></i>
                        ส่ง OTP ใหม่
                      </button>
                    )}
                    
                    {/* Show message when resend is disabled to prevent cost abuse */}
                    {state.disableResend && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle me-1"></i>
                        การส่ง OTP ใหม่ถูกปิดใช้งานเพื่อป้องกันค่าใช้จ่าย
                      </small>
                    )}
                  </div>

                  <div className="row mx--1 mt-2">
                    <div className="col px-1">
                      <Link
                        to="/register"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-1 c-white bdr-10"
                        style={{ textDecoration: 'none' }}
                      >
                        <i className="fas fa-chevron-left me-2"></i> กลับ
                      </Link>
                    </div>
                    <div className="col px-1">
                      <button
                        type="submit"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10 w-100"
                        disabled={isSubmitting || otpCode.length !== 6}
                        style={{ border: 'none' }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            กำลังยืนยัน...
                          </>
                        ) : (
                          'ยืนยันรหัส →'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterOTPPage
